// Admin shell — the same SidebarShell every other role uses, configured for
// administration. Deliberately not a new kind of chrome: an admin who also
// teaches should not have to learn a second navigation.
//
// The nav differs by WHERE the admin is, because the two admin surfaces are
// genuinely different jobs:
//
//   /admin/* at the root domain   platform operator — manages schools and CWY's
//                                 own teaching staff
//   /admin/* at a school URL      school admin — manages that school
//
// `/admin` itself holds no screen: it is the admin login when signed out and a
// forward to the first nav entry when signed in.
//
// A platform operator has no roster or settings to show for any SCHOOL, because
// managing tenants gives them no view inside one — inside a school is Schools →
// Manage, audited support. Classes live there too, per school: the platform
// menu has no Classrooms entry. CWY's own classes (the first-party workspace)
// are reached from the CWY row of the Schools list.
import { useClaims } from '@/hooks/useSession';
import { SidebarShell, type ShellNavItem } from '@/components/SidebarShell';
import { isFirstParty } from '@/lib/tenant';

const PLATFORM_NAV: ShellNavItem[] = [
  { to: '/admin/schools', label: 'Schools', icon: 'home' },
  { to: '/admin/curriculum', label: 'Curriculum', icon: 'book' },
  // CWY's own board papers, for every school — authored like the curriculum.
  { to: '/admin/papers', label: 'Past papers', icon: 'file-text' },
  { to: '/admin/staff', label: 'Staff', icon: 'users' },
];

// Ordered the way a new school is actually set up: choose what you teach, add
// the people who teach it, put them in classrooms, then bring the students in.
// Each step reads the one above it, so the order is not a preference.
const SCHOOL_NAV: ShellNavItem[] = [
  { to: '/admin/curriculum', label: 'Curriculum', icon: 'book' },
  { to: '/admin/staff', label: 'Staff', icon: 'user' },
  { to: '/admin/classrooms', label: 'Classrooms', icon: 'layers' },
  { to: '/admin/students', label: 'Students', icon: 'users' },
  // A shelf per class, so it follows the classes it is stocked for.
  { to: '/admin/books', label: 'Books', icon: 'book' },
  { to: '/admin/settings', label: 'Settings', icon: 'settings' },
  { to: '/admin/audit', label: 'Activity', icon: 'file-text' },
];

export function AdminShell() {
  const claims = useClaims();
  const platform = claims && claims.platformRole !== 'USER';

  // Keeping non-admins out is RequireSurface's job in the route table; by the
  // time this renders the surface already matched. Held until the account lands
  // so the navigation below is never chosen on an empty cache.
  if (!claims) return null;

  // A platform operator at the root domain gets the platform console. The same
  // operator visiting a school URL gets that school's console instead — which is
  // the support path, and is audited server-side.
  const usePlatformNav = Boolean(platform) && isFirstParty;

  return (
    <SidebarShell
      nav={usePlatformNav ? PLATFORM_NAV : SCHOOL_NAV}
      homeTo="/admin"
      collapseKey="cwy-admin-sidebar-collapsed"
      // A platform operator inside a school's own URL had no way back: the
      // school nav, a logo that resolves under the school, and no workspace
      // switcher (they hold no membership). A plain anchor, not a router link —
      // the router's base is /school/:slug, and this must leave it.
      headerExtras={
        platform && !isFirstParty ? (
          <a
            href="/admin/schools"
            className="text-sm font-semibold no-underline"
            style={{ color: 'var(--text-accent)' }}
          >
            ← CWY admin
          </a>
        ) : undefined
      }
    />
  );
}
